import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import { ReactNode, useId, useState } from "react";

interface Props {
  icon: ReactNode;
  title: string;
  /** Heading is a button that folds the body; every section starts folded. */
  collapsible?: boolean;
  children: ReactNode;
}

/** One topic of a help drawer: icon and title, then the body. */
export function HelpSection({ icon, title, collapsible = false, children }: Props) {
  const [open, setOpen] = useState(!collapsible);
  const bodyId = useId();

  const heading = (
    <>
      <Box component="span" aria-hidden sx={{ display: "flex", color: "primary.main", "& svg": { fontSize: 20 } }}>
        {icon}
      </Box>
      <Box component="span" sx={{ flex: 1 }}>
        {title}
      </Box>
    </>
  );

  return (
    <Box component="section" sx={{ borderBottom: 1, borderColor: "divider", "&:last-of-type": { borderBottom: 0 } }}>
      <Box component="h3" sx={{ m: 0, fontSize: 15.5, fontWeight: 650 }}>
        {collapsible ? (
          <Box
            component="button"
            type="button"
            onClick={() => setOpen(current => !current)}
            aria-expanded={open}
            aria-controls={bodyId}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              width: "100%",
              py: 1.75,
              px: 0,
              border: 0,
              bgcolor: "transparent",
              font: "inherit",
              color: "inherit",
              textAlign: "left",
              cursor: "pointer",
              borderRadius: 1,
            }}
          >
            {heading}
            <ExpandMoreIcon
              aria-hidden
              sx={{
                fontSize: 20,
                color: "text.secondary",
                transform: open ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform .15s",
                "@media (prefers-reduced-motion: reduce)": { transition: "none" },
              }}
            />
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 1.75 }}>{heading}</Box>
        )}
      </Box>
      <Collapse in={open}>
        <Box id={bodyId} sx={{ pb: 2 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}
